import React, { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import styled from 'styled-components';
import { Button, Form, message, Select, Spin } from 'udesk-ui';
import { getCompanysBusiModel, postCompanysBusiModel } from 'src/api/companys/busi/model';
import { getCompanysBusiModelAppAndModel } from 'src/api/companys/busi/model/appAndModel';
import UdeskLocales from 'UdeskLocales';

const Wrapper = styled.div``;

// 模型名称后的等级标识
const ModelLevel = styled.span`
    margin-left: 8px;
    color: rgba(0, 0, 0, 0.45);
`;

interface FormModel {
    intelligentModel?: number;
    smartLabel?: number;
    businessSummary?: number;
    problemExtraction?: number;
    intelligentExtraction?: number;
    semanticTag?: number;
    translation?: number;
    imageUnderstanding?: number;
    audioComprehension?: number;
    videoComprehension?: number;
    documentUnderstanding?: number;
}

interface TemplateProps {}

const Template: FC<TemplateProps> = memo(() => {
    const [form] = Form.useForm<FormModel>();

    const { data: companysBusiModelData } = useRequest(getCompanysBusiModel);

    const { audioOptions, imageOptions, textOptions, videoOptions } = useMemo(() => {
        // 模型等级（modelLevel，如 T1/T2）决定计费标准，直接把等级显示在下拉项里
        const buildOptions = (list: any[]) =>
            (list || []).map((item) => ({
                label: (
                    <span>
                        {item.name}
                        {item.modelLevel ? <ModelLevel>{item.modelLevel}</ModelLevel> : null}
                    </span>
                ),
                value: item.id,
            }));

        const data = (companysBusiModelData?.data as any) || {};

        return {
            audioOptions: buildOptions(data.audio),
            imageOptions: buildOptions(data.image),
            textOptions: buildOptions(data.text),
            videoOptions: buildOptions(data.video),
        };
    }, [companysBusiModelData]);

    // 模型等级（后端 modelLevel，如 T1/T2）决定计费标准，切换前后等级不一致时提醒用户
    const modelLevelMap = useMemo(() => {
        const map: Record<number, string> = {};
        const data = (companysBusiModelData?.data as any) || {};
        Object.keys(data).forEach((key) => {
            if (Array.isArray(data[key])) {
                data[key].forEach((item: any) => {
                    if (item?.id != null && item.modelLevel) {
                        map[item.id] = item.modelLevel;
                    }
                });
            }
        });
        return map;
    }, [companysBusiModelData]);

    // 记录每个业务类型当前选中的模型，切换时用的是「切换前」的值
    const selectedModelRef = useRef<Record<string, number>>({});

    const handleModelChange = useCallback(
        (fieldName: string, value: number) => {
            const prevModelId = selectedModelRef.current[fieldName];
            selectedModelRef.current[fieldName] = value;
            if (!prevModelId || !value || prevModelId === value) {
                return;
            }
            const prevLevel = modelLevelMap[prevModelId];
            const nextLevel = modelLevelMap[value];
            if (prevLevel && nextLevel && prevLevel !== nextLevel) {
                message.warning(
                    UdeskLocales['current'].pages.systemIntegration.integrationLLM.index
                        .modelLevelChangedTip
                );
            }
        },
        [modelLevelMap]
    );

    const fields = useMemo(
        () => [
            {
                label: /* 智能模型 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.intelligentModel,
                name: 'intelligentModel',
                busiType: 'INTELLIGENT_MODEL',
                options: textOptions,
            },
            {
                label: /* 智能标签 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.smartLabel,
                name: 'smartLabel',
                busiType: 'INTELLIGENT_TAG',
                options: textOptions,
            },
            {
                label: /* 业务小结 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.businessSummary,
                name: 'businessSummary',
                busiType: 'BUSINESS_SUMMARY',
                options: textOptions,
            },
            {
                label: /* 问题抽取 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.problemExtraction,
                name: 'problemExtraction',
                busiType: 'QUESTION_EXTRACTION',
                options: textOptions,
            },
            {
                label: /* 智能抽取 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.intelligentExtraction,
                name: 'intelligentExtraction',
                busiType: 'INTELLIGENT_EXTRACTION',
                options: textOptions,
            },
            {
                label: /* 语义标签 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.semanticLabel,
                name: 'semanticTag',
                busiType: 'SEMANTIC_ANALYSIS',
                options: textOptions,
            },
            {
                label: /* 翻译 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.translation,
                name: 'translation',
                busiType: 'TRANSLATION',
                options: textOptions,
            },
            {
                label: /* 图片理解 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.pictureUnderstanding,
                name: 'imageUnderstanding',
                busiType: 'IMAGE_RECOGNITION',
                options: imageOptions,
            },
            {
                label: /* 音频理解 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.audioUnderstanding,
                name: 'audioComprehension',
                busiType: 'AUDIO_UNDERSTANDING',
                options: audioOptions,
            },
            {
                label: /* 视频理解 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.videoUnderstanding,
                name: 'videoComprehension',
                busiType: 'VIDEO_UNDERSTANDING',
                options: videoOptions,
            },
            // {
            //     label: '文档理解',
            //     name: 'documentUnderstanding',
            //     busiType: 'WORD_UNDERSTANDING',
            //     options: textOptions,
            // },
        ],
        [audioOptions, imageOptions, textOptions, videoOptions]
    );

    const { refresh, loading: getLoading } = useRequest(getCompanysBusiModelAppAndModel, {
        onSuccess: (res) => {
            const values: FormModel = {};

            res.data?.forEach((l) => {
                if (l.busiType === 'INTELLIGENT_MODEL') {
                    values.intelligentModel = l.modelInfoId;
                }
                if (l.busiType === 'INTELLIGENT_TAG') {
                    values.smartLabel = l.modelInfoId;
                }
                if (l.busiType === 'BUSINESS_SUMMARY') {
                    values.businessSummary = l.modelInfoId;
                }
                if (l.busiType === 'QUESTION_EXTRACTION') {
                    values.problemExtraction = l.modelInfoId;
                }
                if (l.busiType === 'INTELLIGENT_EXTRACTION') {
                    values.intelligentExtraction = l.modelInfoId;
                }
                if (l.busiType === 'SEMANTIC_ANALYSIS') {
                    values.semanticTag = l.modelInfoId;
                }
                if (l.busiType === 'TRANSLATION') {
                    values.translation = l.modelInfoId;
                }
                if (l.busiType === 'IMAGE_RECOGNITION') {
                    values.imageUnderstanding = l.modelInfoId;
                }
                if (l.busiType === 'AUDIO_UNDERSTANDING') {
                    values.audioComprehension = l.modelInfoId;
                }
                if (l.busiType === 'VIDEO_UNDERSTANDING') {
                    values.videoComprehension = l.modelInfoId;
                }
                if (l.busiType === 'WORD_UNDERSTANDING') {
                    values.documentUnderstanding = l.modelInfoId;
                }
            });

            form.setFieldsValue(values);
            selectedModelRef.current = { ...values };
        },
    });

    const [saveLoading, setSaveLoading] = useState(false);

    const onFinish = useCallback(
        (values: FormModel) => {
            form.validateFields().then(() => {
                const busiModelConfigs = fields.map((f) => {
                    return {
                        busiType: f.busiType,
                        modelInfoId: values[f.name],
                    };
                });

                setSaveLoading(true);

                postCompanysBusiModel({ busiModelConfigs })
                    .then(() => {
                        message.success(/* 保存成功 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.saveSuccessfully);
                        refresh();
                    })
                    .finally(() => {
                        setSaveLoading(false);
                    });
            });
        },
        [fields, form, refresh]
    );

    return (
        <Wrapper>
            <Spin spinning={getLoading || saveLoading}>
                <Form
                    form={form}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 12 }}
                    onFinish={onFinish}
                >
                    {fields.map((f) => (
                        <Form.Item
                            key={f.name}
                            label={f.label}
                            name={f.name}
                            rules={[{ required: true, message: `${/* 请选择模型 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.pleaseSelectAModel}` }]}
                        >
                            <Select
                                options={f.options}
                                placeholder={/* 请选择模型 */UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.pleaseSelectAModel}
                                onChange={(value) => handleModelChange(f.name, value as number)}
                            />
                        </Form.Item>
                    ))}

                    <Form.Item wrapperCol={{ offset: 6 }}>
                        <Button type="primary" htmlType="submit">{/* 保存 */}{UdeskLocales['current'].pages.systemIntegration.integrationLLM.index.save}</Button>
                    </Form.Item>
                </Form>
            </Spin>
        </Wrapper>
    );
});

export default Template;
